import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  afterNextRender,
  computed,
  effect,
  inject,
  Injector,
  input,
  linkedSignal,
  output,
  signal,
  untracked,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { A11yModule } from '@angular/cdk/a11y';
import { MatError, MatFormField, MatLabel } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { MatSelect } from '@angular/material/select';
import { ErrorStateMatcher, MatOption } from '@angular/material/core';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { AccountService } from '../../../account/account.service';
import {
  ablaufZeitpunkt,
  CreatePatRequest,
  EXTRA_ACTION_DEPENDENCIES,
  kontoRechteChips,
  TokenAblauf,
} from '../../../models/pat.model';

const STANDARD_ABLAUF: TokenAblauf = 90;
const STANDARD_RECHTE: readonly string[] = ['project:read'];

// Wie im Agenten-Dialog: „verknüpfen“ ruft Coverage-Endpunkte, die zusätzlich Lesen auf Testfällen verlangen.
const MITGEWAEHLT: Readonly<Record<string, string>> = Object.fromEntries(
  Object.entries(EXTRA_ACTION_DEPENDENCIES).map(([scope, dep]) => [scope, `${dep.resource}:${dep.level}`]),
);

@Component({
  selector: 'app-token-anlegen-dialog',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    A11yModule,
    MatFormField,
    MatLabel,
    MatError,
    MatInput,
    MatSelect,
    MatOption,
    MatProgressSpinner,
    ReactiveFormsModule,
    TranslatePipe,
  ],
  templateUrl: './token-anlegen-dialog.component.html',
  styleUrl: './token-anlegen-dialog.component.scss',
})
export class TokenAnlegenDialogComponent {
  private readonly accountService = inject(AccountService);
  private readonly translate = inject(TranslateService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly injector = inject(Injector);

  readonly offen = input.required<boolean>();
  readonly busy = input(false);
  /** Übersetzungsschlüssel der Meldung am Namensfeld. */
  readonly nameFehler = input<string | null>(null);
  readonly anlegen = output<CreatePatRequest>();
  readonly geschlossen = output<void>();

  protected readonly ablaufOptionen: readonly { wert: TokenAblauf; key: string }[] = [
    { wert: 30, key: '30' },
    { wert: 90, key: '90' },
    { wert: 365, key: '365' },
    { wert: null, key: 'kein' },
  ];

  protected readonly katalogZustand = signal<'offen' | 'laedt' | 'geladen' | 'fehler'>('offen');
  protected readonly rechte = signal<string[]>([]);
  protected readonly nameControl = new FormControl('', { nonNullable: true });
  protected readonly name = signal('');
  protected readonly ablauf = signal<TokenAblauf>(STANDARD_ABLAUF);
  protected readonly gewaehlt = signal<string[]>([...STANDARD_RECHTE]);
  protected readonly sichtbarerNameFehler = linkedSignal(() => this.nameFehler());
  protected readonly nameFehlerMatcher: ErrorStateMatcher = {
    isErrorState: () => this.sichtbarerNameFehler() !== null,
  };

  protected readonly gesperrt = computed(() => {
    const gewaehlt = this.gewaehlt();
    return new Set(
      Object.keys(MITGEWAEHLT)
        .filter((scope) => gewaehlt.includes(scope))
        .map((scope) => MITGEWAEHLT[scope]),
    );
  });

  protected readonly kannAnlegen = computed(
    () =>
      this.katalogZustand() === 'geladen' && this.name().trim() !== '' && this.gewaehlt().length > 0 && !this.busy(),
  );

  private readonly nameFeld = viewChild<ElementRef<HTMLInputElement>>('nameFeld');
  private readonly ablaufAuswahl = viewChild<MatSelect>('ablaufAuswahl');

  constructor() {
    effect(() => {
      if (this.offen()) untracked(() => this.zuruecksetzen());
    });
  }

  protected label(scope: string): string {
    const key = `pat.scope.${scope}`;
    const text = this.translate.instant(key);
    return text === key ? scope : text;
  }

  protected istGewaehlt(scope: string): boolean {
    return this.gewaehlt().includes(scope);
  }

  protected umschalten(scope: string): void {
    if (this.gesperrt().has(scope)) return;
    const aktuell = this.gewaehlt();
    if (aktuell.includes(scope)) {
      this.gewaehlt.set(aktuell.filter((s) => s !== scope));
      return;
    }
    const mit = MITGEWAEHLT[scope];
    this.gewaehlt.set([...new Set([...aktuell, scope, ...(mit ? [mit] : [])])]);
  }

  protected nameEingabe(): void {
    this.name.set(this.nameControl.value);
    this.sichtbarerNameFehler.set(null);
  }

  protected bestaetigen(): void {
    if (!this.kannAnlegen()) return;
    const gewaehlt = this.gewaehlt();
    this.anlegen.emit({
      name: this.name().trim(),
      scopes: this.rechte().filter((scope) => gewaehlt.includes(scope)),
      expiresAt: ablaufZeitpunkt(this.ablauf(), new Date()),
    });
  }

  protected schliessen(): void {
    if (this.busy()) return;
    this.geschlossen.emit();
  }

  // Das Auswahl-Overlay schließt erst am body; ohne diese Prüfung ginge der Dialog samt Eingaben mit zu.
  protected escape(): void {
    if (this.ablaufAuswahl()?.panelOpen) return;
    this.schliessen();
  }

  protected katalogLaden(): void {
    this.katalogZustand.set('laedt');
    this.accountService
      .getTokenScopes()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (katalog) => {
          const rechte = kontoRechteChips(katalog);
          this.rechte.set(rechte);
          this.katalogZustand.set(rechte.length > 0 ? 'geladen' : 'fehler');
        },
        error: () => this.katalogZustand.set('fehler'),
      });
  }

  private zuruecksetzen(): void {
    this.nameControl.setValue('');
    this.name.set('');
    this.ablauf.set(STANDARD_ABLAUF);
    this.gewaehlt.set([...STANDARD_RECHTE]);
    this.sichtbarerNameFehler.set(null);
    const zustand = this.katalogZustand();
    if (zustand === 'offen' || zustand === 'fehler') this.katalogLaden();
    afterNextRender(() => this.nameFeld()?.nativeElement.focus(), { injector: this.injector });
  }
}
